import React from 'react';
import Icon from '../../../components/AppIcon';

const ServicePreview = ({ formData, serviceNumber }) => {
  if (!formData) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <div className="text-center">
            <Icon name="Eye" size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Preview will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  const formatPrice = () => {
    if (formData?.pricing?.type === 'fixed') {
      return `£${formData?.pricing?.fixedPrice}`;
    }
    if (formData?.pricing?.type === 'range') {
      return `£${formData?.pricing?.minPrice} - £${formData?.pricing?.maxPrice}`;
    }
    if (formData?.pricing?.type === 'donation') {
      return formData?.pricing?.suggestedDonation 
        ? `Suggested donation: £${formData?.pricing?.suggestedDonation}` 
        : 'By donation';
    }
    return 'Price TBD';
  };

  const formatSchedule = () => {
    if (formData?.schedule?.eventType === 'one-time') {
      return `${formData?.schedule?.startDate || 'Date TBD'} at ${formData?.schedule?.startTime || 'Time TBD'}`;
    }
    if (formData?.schedule?.eventType === 'recurring') {
      return `${formData?.schedule?.frequency || 'Recurring'} from ${formData?.schedule?.startDate || 'Date TBD'}`;
    }
    if (formData?.schedule?.eventType === 'course') {
      return `${formData?.schedule?.courseWeeks || 'Multi'} week course starting ${formData?.schedule?.courseStartDate || 'Date TBD'}`;
    }
    return 'Schedule TBD';
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Service Header */}
      <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                Service {serviceNumber}
              </span>
              {formData?.category && (
                <span className="text-xs text-muted-foreground capitalize">
                  {formData?.category?.replace('-', ' & ')}
                </span>
              )}
            </div>
            <h3 className="font-heading font-semibold text-foreground text-lg leading-tight">
              {formData?.name || 'Untitled Service'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              by {formData?.businessName || 'Business Name'}
            </p>
          </div>
        </div>
      </div>
      {/* Service Content */}
      <div className="p-4 space-y-4">
        {/* Description */}
        {formData?.shortDescription && (
          <p className="text-sm text-muted-foreground">
            {formData?.shortDescription}
          </p>
        )}

        {/* Key Details */}
        <div className="space-y-3">
          {/* Pricing */}
          <div className="flex items-center space-x-2">
            <Icon name="PoundSterling" size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {formatPrice()}
            </span>
          </div>

          {/* Schedule */}
          <div className="flex items-start space-x-2">
            <Icon name="Calendar" size={14} className="text-muted-foreground mt-0.5" />
            <span className="text-sm text-muted-foreground">
              {formatSchedule()}
            </span>
          </div>

          {/* Location */}
          <div className="flex items-start space-x-2">
            <Icon name="MapPin" size={14} className="text-muted-foreground mt-0.5" />
            <span className="text-sm text-muted-foreground">
              {formData?.location?.isOnline 
                ? 'Online Event'
                : formData?.location?.city 
                  ? `${formData?.location?.city}${formData?.location?.postcode ? `, ${formData?.location?.postcode}` : ''}`
                  : 'Location TBD'
              }
            </span>
          </div>

          {/* Capacity */}
          {formData?.schedule?.capacity && (
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Max {formData?.schedule?.capacity} participants
              </span>
            </div>
          )}
        </div>

        {/* Tags */}
        {formData?.tags && (
          <div className="flex flex-wrap gap-1 pt-2 border-t border-border">
            {formData?.tags?.split(',')?.slice(0, 3)?.map((tag, index) => (
              <span 
                key={index}
                className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground"
              >
                {tag?.trim()}
              </span>
            ))}
          </div>
        )}

        {/* Completion Status */}
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Completion</span>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5]?.map((step) => {
                const isCompleted = step === 1 && (formData?.name && formData?.category) ||
                                  step === 2 && (formData?.location?.isOnline || formData?.location?.city) ||
                                  step === 3 && formData?.pricing?.type ||
                                  step === 4 && formData?.schedule?.eventType ||
                                  step === 5;
                
                return (
                  <div
                    key={step}
                    className={`w-2 h-2 rounded-full ${
                      isCompleted ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePreview;